import React, { useState } from 'react';
import axios from 'axios';
import {
  FiCheckCircle, FiXCircle, FiMinusCircle, FiClock,
  FiAlertTriangle, FiArchive
} from 'react-icons/fi';
import { API_URL } from '../../../utils/authUtils';

/**
 * FeedbackClosurePanel - Handles terminal states and closure.
 *
 * Active for: CONNECTED (close action), CLOSED, EXPIRED, DECLINED, WITHDRAWN
 *
 * For CONNECTED: Close request form with outcome dropdown + reason
 * For terminal states: Read-only summary of lifecycle and outcome
 */

const OUTCOMES = [
  { value: 'successful', label: 'Successful', description: 'Introduction completed successfully', icon: FiCheckCircle, color: 'text-green-600' },
  { value: 'unsuccessful', label: 'Unsuccessful', description: 'Attempted but didn\'t work out', icon: FiXCircle, color: 'text-red-600' },
  { value: 'withdrawn', label: 'Withdrawn', description: 'Requester withdrew after processing', icon: FiMinusCircle, color: 'text-gray-600' },
  { value: 'expired', label: 'Expired', description: 'Request or consent expired', icon: FiClock, color: 'text-orange-600' },
  { value: 'cancelled', label: 'Cancelled', description: 'Staff cancelled for operational reasons', icon: FiAlertTriangle, color: 'text-yellow-600' },
];

const TERMINAL_STATUSES = ['CLOSED', 'EXPIRED', 'DECLINED', 'WITHDRAWN'];

const FeedbackClosurePanel = ({ requestDetail, requestId, onRequestUpdated, authHeaders }) => {
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Close form
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [outcome, setOutcome] = useState('successful');
  const [closeReason, setCloseReason] = useState('');
  const [closeLoading, setCloseLoading] = useState(false);

  const getErrorMessage = (err, defaultMsg) => {
    if (err.response?.data) {
      const detail = err.response.data.detail;
      if (typeof detail === 'string') return detail;
      if (detail?.message) return detail.message;
      if (detail?.error) return detail.error;
    }
    return defaultMsg;
  };

  const closeRequest = async () => {
    if (!closeReason.trim()) {
      setError('Please provide a reason for closing');
      return;
    }
    setCloseLoading(true);
    setError(null);
    try {
      await axios.post(
        `${API_URL}/api/staff/brokerage/requests/${requestId}/close`,
        {
          outcome,
          close_reason: closeReason,
          row_version: requestDetail.row_version,
        },
        authHeaders()
      );
      setSuccessMsg('Request closed successfully');
      setShowCloseForm(false);
      onRequestUpdated();
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Conflict: Modified by another user. Refreshing...');
        onRequestUpdated();
      } else {
        setError(getErrorMessage(err, 'Failed to close request'));
      }
    } finally {
      setCloseLoading(false);
    }
  };

  const isTerminal = TERMINAL_STATUSES.includes(requestDetail.status);
  const isConnected = requestDetail.status === 'CONNECTED';

  const getStatusIcon = () => {
    switch (requestDetail.status) {
      case 'CLOSED': return <FiArchive className="w-6 h-6 text-gray-500" />;
      case 'EXPIRED': return <FiClock className="w-6 h-6 text-orange-500" />;
      case 'DECLINED': return <FiXCircle className="w-6 h-6 text-red-500" />;
      case 'WITHDRAWN': return <FiMinusCircle className="w-6 h-6 text-gray-500" />;
      default: return <FiCheckCircle className="w-6 h-6 text-green-500" />;
    }
  };

  const getLifecycleDuration = () => {
    if (!requestDetail.created_at) return null;
    const start = new Date(requestDetail.created_at);
    const end = requestDetail.updated_at ? new Date(requestDetail.updated_at) : new Date();
    const diffMs = end - start;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}, ${hours}h`;
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-1">
          {isTerminal ? 'Request Summary' : 'Close Request'}
        </h3>
        <p className="text-xs text-gray-500">
          {isTerminal
            ? 'This request has reached a terminal state.'
            : 'Close this request with a final outcome.'}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {successMsg && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {successMsg}
        </div>
      )}

      {/* Terminal state summary */}
      {isTerminal && (
        <div className={`border rounded-lg p-5 ${
          requestDetail.status === 'CLOSED' ? 'border-gray-300 bg-gray-50' :
          requestDetail.status === 'DECLINED' ? 'border-red-200 bg-red-50' :
          requestDetail.status === 'EXPIRED' ? 'border-orange-200 bg-orange-50' :
          'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            {getStatusIcon()}
            <div>
              <p className="text-lg font-semibold text-gray-800">{requestDetail.status}</p>
              <p className="text-sm text-gray-500">
                {requestDetail.updated_at && `Closed ${new Date(requestDetail.updated_at).toLocaleDateString(undefined, {
                  month: 'long', day: 'numeric', year: 'numeric'
                })}`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Lifecycle Duration */}
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase mb-1">Lifecycle Duration</p>
              <p className="text-sm font-medium text-gray-800">{getLifecycleDuration() || 'N/A'}</p>
            </div>

            {/* Outcome */}
            {requestDetail.outcome && (
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <p className="text-xs text-gray-500 uppercase mb-1">Outcome</p>
                <p className="text-sm font-medium text-gray-800 capitalize">{requestDetail.outcome}</p>
              </div>
            )}

            {/* Decline Reason */}
            {requestDetail.decline_reason_notes && (
              <div className="bg-white rounded-lg p-3 border border-gray-100 col-span-full">
                <p className="text-xs text-gray-500 uppercase mb-1">
                  {requestDetail.status === 'DECLINED' ? 'Decline Reason' : 'Close Reason'}
                </p>
                <p className="text-sm text-gray-700">{requestDetail.decline_reason_notes}</p>
              </div>
            )}

            {/* Requester */}
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase mb-1">Requester</p>
              <p className="text-sm font-medium text-gray-800">{requestDetail.requester_name || 'Unknown'}</p>
            </div>

            {/* Target */}
            <div className="bg-white rounded-lg p-3 border border-gray-100">
              <p className="text-xs text-gray-500 uppercase mb-1">Target</p>
              <p className="text-sm font-medium text-gray-800">{requestDetail.target_name || 'Not selected'}</p>
            </div>

            {/* Staff Notes */}
            {requestDetail.staff_notes && (
              <div className="bg-white rounded-lg p-3 border border-gray-100 col-span-full">
                <p className="text-xs text-gray-500 uppercase mb-1">Staff Notes</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{requestDetail.staff_notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Close form for CONNECTED status */}
      {isConnected && (
        <div className="border border-gray-200 rounded-lg p-4">
          {!showCloseForm ? (
            <div className="text-center py-3">
              <p className="text-sm text-gray-500 mb-3">
                The introduction is complete. Close this request when ready.
              </p>
              <button
                onClick={() => setShowCloseForm(true)}
                className="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-1 mx-auto"
              >
                <FiArchive className="w-4 h-4" /> Close Request
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-xs font-medium text-gray-500 uppercase">Close Request</p>

              {/* Outcome Selection */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Outcome</label>
                <div className="space-y-2">
                  {OUTCOMES.map(o => {
                    const Icon = o.icon;
                    return (
                      <label key={o.value} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                        outcome === o.value ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="outcome"
                          value={o.value}
                          checked={outcome === o.value}
                          onChange={() => setOutcome(o.value)}
                          className="text-indigo-600"
                        />
                        <Icon className={`w-4 h-4 ${o.color}`} />
                        <div>
                          <span className="text-sm font-medium text-gray-800">{o.label}</span>
                          <p className="text-xs text-gray-500">{o.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
                <textarea
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  rows={3}
                  className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Provide details about why this request is being closed..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={closeRequest}
                  disabled={closeLoading || !closeReason.trim()}
                  className="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
                >
                  {closeLoading ? 'Closing...' : 'Confirm Close'}
                </button>
                <button
                  onClick={() => { setShowCloseForm(false); setCloseReason(''); setOutcome('successful'); }}
                  className="px-4 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackClosurePanel;
